import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CrawlerPageLayout } from "pages/crawler/components/CrawlerPageLayout";
import { RecentlyViewedProvider } from "providers/RecentlyViewed/RecentlyViewedProvider";
import Button from "components/Button";
import CrawlerSummaryModal from "pages/crawler/components/form/CrawlerSummaryModal";
import scraperApi from "api";
import Toaster from "components/Toaster";
import { useCrawler } from "providers/CrawlerProvider";
import { mapFormDataToCrawlerData } from "pages/crawler/utils/crawlerFormUtils";
import { useCrawlerForm } from "pages/crawler/hooks/useCrawlerForm";
import { CrawlerCostProvider } from "pages/crawler/providers/CrawlerCostProvider";
import { validateCrawlerName, validateCrawlerLimits } from "pages/crawler/utils/crawlerValidation";
import { generateUniqueCrawlerName } from "pages/crawler/utils/crawlerNameGenerator";
import { CrawlerUrlInput } from "pages/crawler/components/form/CrawlerUrlInput";
import { CrawlerFormSections } from "pages/crawler/components/form/CrawlerFormSections";
import { CrawlerFormData, DEFAULT_CRAWLER_FORM_VALUES } from "pages/crawler/types";

const CreateCrawlerPage = () => {
  const [crawlerName, setCrawlerName] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const navigate = useNavigate();
  const { crawlers, refreshCrawlers } = useCrawler();

  // Use shared crawler form hook
  const {
    isTestingCallback,
    callbackTestResult,
    setCallbackTestResult,
    testCallback: testCallbackFn,
    scrollToFirstError,
  } = useCrawlerForm();

  const methods = useForm<CrawlerFormData>({
    defaultValues: DEFAULT_CRAWLER_FORM_VALUES,
    mode: "onChange", // Enable real-time validation
  });

  const {
    register,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = methods;
  const watchedValues = watch();

  // Generate unique crawler name based on existing crawlers
  useEffect(() => {
    if (!crawlerName && crawlers) {
      const existingNames = crawlers.map((crawler: any) => crawler.name || "");
      const uniqueName = generateUniqueCrawlerName(existingNames);
      setCrawlerName(uniqueName);
      const currentSchedule = getValues("schedule");
      setValue("schedule", { ...currentSchedule, name: uniqueName }, { shouldDirty: false });
    }
  }, [crawlers, crawlerName, getValues, setValue]);

  // Keep crawler name state in sync with schedule name
  useEffect(() => {
    const scheduleName = watchedValues?.schedule?.name;
    if (scheduleName && scheduleName !== crawlerName) {
      setCrawlerName(scheduleName);
    }
  }, [watchedValues?.schedule?.name, crawlerName]);

  const handleCrawlerNameChange = (newCrawlerName: string) => {
    setCrawlerName(newCrawlerName);
    const currentSchedule = getValues("schedule");
    setValue("schedule", { ...currentSchedule, name: newCrawlerName }, { shouldDirty: true, shouldValidate: true });
  };

  const onSubmit = async (data: CrawlerFormData) => {
    // Validate crawler name
    const nameError = validateCrawlerName(crawlerName);
    if (nameError) {
      Toaster.error(nameError);
      return;
    }

    // Validate crawler limits
    const limitsError = validateCrawlerLimits(data.linkDepth, data.maxCost);
    if (limitsError) {
      Toaster.error(limitsError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Use shared data mapping utility
      const crawlerData = mapFormDataToCrawlerData(data, crawlerName);

      const response = await scraperApi.crawlers.createCrawler(crawlerData);

      if (response.success) {
        Toaster.success(`Crawler "${crawlerName}" created successfully!`);
        // Refresh the crawler list to show the new crawler
        await refreshCrawlers();
        navigate("/crawlers");
      } else {
        throw new Error(response.message || "Failed to create crawler");
      }
    } catch (error: any) {
      console.error("Error creating crawler:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create crawler";
      Toaster.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scheduleJob = async () => {
    // Trigger validation before showing summary
    const isFormValid = await methods.trigger();

    if (!isFormValid) {
      scrollToFirstError();
      Toaster.error("Please fix the errors highlighted");
      return;
    }

    // Show summary modal for review
    setShowSummaryModal(true);
  };

  const confirmScheduleJob = () => {
    setShowSummaryModal(false);
    handleSubmit(onSubmit)();
  };

  const testCallback = () => testCallbackFn(watchedValues.callbackUrl);

  return (
    <RecentlyViewedProvider>
      <CrawlerPageLayout
        crawlerName={crawlerName}
        callback={handleCrawlerNameChange}
        actionButtons={
          <div className="flex justify-end gap-3 pr-6">
            <Button
              text="CANCEL"
              className="button button-secondary rounded"
              onClick={() => navigate("/crawlers")}
              disabled={isSubmitting}
            />
            <Button
              text="REVIEW CHANGES"
              className="button button-primary rounded"
              onClick={scheduleJob}
              disabled={isSubmitting}
            />
          </div>
        }
      >
        <FormProvider {...methods}>
          <CrawlerCostProvider formValues={watchedValues}>
            <CrawlerUrlInput
              register={register}
              setValue={setValue}
              errors={errors}
              watchedValues={watchedValues}
            />

            <CrawlerFormSections
              register={register}
              setValue={setValue}
              errors={errors}
              watchedValues={watchedValues}
              testCallback={testCallback}
              isTestingCallback={isTestingCallback}
              callbackTestResult={callbackTestResult}
              setCallbackTestResult={setCallbackTestResult}
            />
          </CrawlerCostProvider>
        </FormProvider>

        {/* Summary Modal */}
        <CrawlerSummaryModal
          isOpen={showSummaryModal}
          onClose={() => setShowSummaryModal(false)}
          onConfirm={confirmScheduleJob}
          formValues={watchedValues}
          crawlerName={crawlerName}
        />
      </CrawlerPageLayout>
    </RecentlyViewedProvider>
  );
};

export default CreateCrawlerPage;
